import {
  AgentArchitecture,
  ConfigVoiceConfig,
  ModelConfig,
  TestLLMOptions,
  TestResult,
  TestRun,
  TestType,
  VendorModel,
} from '@vendasta/protos/ai_assistants/v1alpha1';
import { TranslateService } from '@ngx-translate/core';
import { getVoiceFamilies } from '../../core/services/ai-assistant-utils';

export function capitalizeFirst(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export interface LLMOptionPair {
  model?: string;
  reasoningEffort?: string;
}

export interface RunVariant {
  llmOptions?: LLMOptionPair;
  voiceConfig?: ConfigVoiceConfig;
}

/**
 * Formats the model and reasoning effort from LLM options into a display string.
 * Uses the logic from test-run-table.component.ts as reference.
 *
 * @param llmOptions - The LLM options containing model and/or reasoning effort
 * @param modelNameLookup - Optional map from model ID to human-readable model name
 * @returns Formatted string like "model (Reasoning)", "model", "Reasoning", or "Default"
 */
export function formatModelAndReasoning(
  llmOptions?: TestLLMOptions | null,
  modelNameLookup?: Map<string, string>,
): string {
  if (!llmOptions) {
    return 'Default';
  }

  const modelId = llmOptions.model || '';
  const reasoningEffort = llmOptions.reasoningEffort || '';

  // Get human-readable model name if lookup is provided
  let modelDisplay = modelId;
  if (modelId && modelNameLookup) {
    modelDisplay = modelNameLookup.get(modelId) || modelId;
  }

  if (modelDisplay && reasoningEffort) {
    return `${modelDisplay} (${capitalizeFirst(reasoningEffort)})`;
  } else if (modelDisplay) {
    return modelDisplay;
  } else if (reasoningEffort) {
    return capitalizeFirst(reasoningEffort);
  } else {
    return 'Default';
  }
}

/**
 * Formats the model and reasoning effort from a TestResult.
 * Convenience wrapper around formatModelAndReasoning.
 *
 * @param testResult - The test result containing LLM options
 * @param modelNameLookup - Optional map from model ID to human-readable model name
 * @returns Formatted string like "model (Reasoning)", "model", "Reasoning", or "Default"
 */
export function formatModelAndReasoningFromTestResult(
  testResult: TestResult,
  modelNameLookup?: Map<string, string>,
): string {
  return formatModelAndReasoning(testResult.llmOptions, modelNameLookup);
}

export function getRunName(run: TestRun | null): string {
  if (!run) return '';
  return run.name || `Run ${run.id?.slice(0, 8) || ''}`;
}

export interface TestStatus {
  name: string;
  icon: string;
  class: string;
}

export const StatusMap = new Map<string, TestStatus>([
  ['initializing', { name: 'AI_ASSISTANT.TESTING.STATUS.INITIALIZING', icon: 'hourglass_empty', class: 'running' }],
  ['in_progress', { name: 'AI_ASSISTANT.TESTING.STATUS.IN_PROGRESS', icon: 'pending', class: 'running' }],
  ['success', { name: 'AI_ASSISTANT.TESTING.STATUS.SUCCESS', icon: 'check_circle', class: 'success' }],
  ['failure', { name: 'AI_ASSISTANT.TESTING.STATUS.FAILURE', icon: 'error', class: 'failure' }],
]);

export function clampIterations(value: number): number {
  return Math.max(1, Math.min(25, value || 1));
}

export const snackbarDuration = 5000;

export function isVoiceTest(testType: TestType | undefined | null): boolean {
  return testType === TestType.TEST_TYPE_VOICE;
}

export function getTestTypeName(testType: TestType, translateService: TranslateService): string {
  switch (testType) {
    case TestType.TEST_TYPE_VOICE:
      return translateService.instant('AI_ASSISTANT.TESTING.TEST_TYPE.VOICE');
    case TestType.TEST_TYPE_CHAT:
    default:
      return translateService.instant('AI_ASSISTANT.TESTING.TEST_TYPE.CHAT');
  }
}

export function getAgentArchitectureName(architecture: AgentArchitecture, translateService: TranslateService): string {
  switch (architecture) {
    case AgentArchitecture.REACT:
      return translateService.instant('AI_ASSISTANT.TESTING.ARCHITECTURE.REACT');
    case AgentArchitecture.SUPERVISOR:
      return translateService.instant('AI_ASSISTANT.TESTING.ARCHITECTURE.SUPERVISOR');
    case AgentArchitecture.STANDARD:
      return translateService.instant('AI_ASSISTANT.TESTING.ARCHITECTURE.STANDARD');
    case AgentArchitecture.DEEP_RESEARCH:
      return translateService.instant('AI_ASSISTANT.TESTING.ARCHITECTURE.DEEP_RESEARCH');
    default:
      return translateService.instant('AI_ASSISTANT.TESTING.ARCHITECTURE.UNSET');
  }
}

function getVoiceValue(vendor: VendorModel, mc: ModelConfig | undefined): string | undefined {
  switch (vendor) {
    case VendorModel.VENDOR_MODEL_DEEPGRAM:
      return mc?.deepgramConfig?.voice;
    case VendorModel.VENDOR_MODEL_ELEVEN_LABS:
      return mc?.elevenLabsConfig?.voice;
    case VendorModel.VENDOR_MODEL_OPEN_AI_REALTIME:
      return mc?.openaiRealtimeConfig?.voice;
    case VendorModel.VENDOR_MODEL_OPEN_AI:
      return mc?.openaiConfig?.voice;
    default:
      return undefined;
  }
}

/**
 * Formats a proto ConfigVoiceConfig into a "Family - Voice" display string (e.g. "Deepgram Aura-2 - Apollo").
 * Returns an empty string when the config is missing or can't be matched to a known voice family.
 */
export function formatVoiceConfig(voiceConfig: ConfigVoiceConfig | undefined | null): string {
  if (!voiceConfig) return '';

  const voiceValue = getVoiceValue(voiceConfig.vendorModel, voiceConfig.modelConfig);
  if (!voiceValue) return '';

  const families = getVoiceFamilies();
  const matchByVendor = families.find(
    (f) => f.provider === voiceConfig.vendorModel && f.voices.some((v) => v.value === voiceValue),
  );
  const family = matchByVendor ?? families.find((f) => f.voices.some((v) => v.value === voiceValue));
  if (!family) return voiceValue;

  const voice = family.voices.find((v) => v.value === voiceValue);
  return voice?.name ? `${family.name} - ${voice.name}` : family.name;
}

export function formatCallDuration(durationNanos: number): string {
  if (!durationNanos) return '-';
  const totalSeconds = Math.round(durationNanos / 1_000_000_000);
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}
